import React from 'react'
import classNames from 'classnames'
import { Extraction, ExtractedTable } from '../types'
import {
  confidenceTone,
  formatConfidence,
  formatDateTime,
  groupFields
} from '../utils'

type Props = { extraction: Extraction }

const Confidence: React.FC<{ value?: number }> = ({ value }) => {
  const label = formatConfidence(value)
  if (!label) return null
  return (
    <span
      className={`dx-conf dx-conf--${confidenceTone(value)}`}
      title={`Document AI confidence ${label}`}
    >
      {label}
    </span>
  )
}

const LineItems: React.FC<{ table: ExtractedTable }> = ({ table }) => (
  <section className="dx-section">
    <h3 className="dx-section__title">
      {table.title}
      <span className="dx-count">{table.rows.length}</span>
    </h3>
    <div className="dx-table-wrap dx-table-wrap--inner">
      <table className="dx-table dx-table--compact">
        <thead>
          <tr>
            <th scope="col" className="dx-num">
              #
            </th>
            {table.columns.map((c) => (
              <th
                key={c.key}
                scope="col"
                className={classNames({ 'dx-num': c.numeric })}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row, i) => (
            // Rows have no identity of their own; order is meaningful.
            // eslint-disable-next-line react/no-array-index-key
            <tr key={i}>
              <td className="dx-num dx-muted">{i + 1}</td>
              {table.columns.map((c) => (
                <td
                  key={c.key}
                  className={classNames({ 'dx-num dx-nowrap': c.numeric })}
                >
                  {row[c.key] ?? <span className="dx-muted">—</span>}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </section>
)

/** What Document AI read from the file, grouped like the document itself. */
const ExtractedDataPanel: React.FC<Props> = ({ extraction }) => {
  const groups = groupFields(extraction.fields)
  return (
    <div className="dx-extracted">
      <div className="dx-extracted__meta">
        {extraction.documentLabel && (
          <span className="dx-chip">{extraction.documentLabel}</span>
        )}
        {extraction.averageConfidence != null && (
          <span className="dx-extracted__conf">
            Average confidence{' '}
            <Confidence value={extraction.averageConfidence} />
          </span>
        )}
        {extraction.extractedAt && (
          <span className="dx-muted">
            Read {formatDateTime(extraction.extractedAt)}
          </span>
        )}
      </div>

      {groups.map(({ group, items }) => (
        <section key={group} className="dx-section">
          <h3 className="dx-section__title">{group}</h3>
          <dl className="dx-fields">
            {items.map((field) => (
              <div
                key={field.key}
                className={classNames('dx-field', {
                  'dx-field--wide': (field.value?.length ?? 0) > 40
                })}
              >
                <dt>
                  {field.label}
                  <Confidence value={field.confidence} />
                </dt>
                <dd className={classNames({ 'dx-muted': !field.value })}>
                  {field.value ?? 'Not found'}
                </dd>
              </div>
            ))}
          </dl>
        </section>
      ))}

      {extraction.tables.map((table) => (
        <LineItems key={table.key} table={table} />
      ))}
    </div>
  )
}

export default ExtractedDataPanel
